import { useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage } from 'react-intl'
import classnames from 'classnames'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Actions
import { addSnoozeAnswer } from '../../../../../actions/home'

// Components
import { If, Text } from '../../../..'
import { IconCheckCircle } from '../../../../shared/Icon'

// Selectors
import { selectClientSnoozedReminder } from '../../../../../store/selectors/home/answers'
import { selectClientHasConfirmedInsurancePolicyStartDate } from '../../../../../store/selectors/homeInsurance'

// Constants
import { OVERLAY_HELPERS } from '../../../../../constants/navigation'
import {
  EVENTS_LABELS,
  ConfirmDetailsClick,
  HomeInsuranceSnoozeClick,
} from '../../../../../constants/trackingEvents'

// Utils
import { dispatchEvent } from '../../../../../helpers/tagManager'
import history from '../../../../../history'

// Assets
import snoozeImg from './snooze.png'
import confirmImg from './confirm.png'

// Styles
import styles from './ConfirmOrSnoozeSection.module.scss'

const ConfirmOrSnoozeSection = () => {
  const { ampli } = useContext(AmplitudeContext)
  const dispatch = useDispatch()

  const clientHasConfirmedPolicyDate = useSelector(
    selectClientHasConfirmedInsurancePolicyStartDate
  )
  const reminderSnoozed = useSelector(selectClientSnoozedReminder)

  const handleAddHomeAnswer = () => {
    if (reminderSnoozed) {
      return
    }
    dispatchEvent(HomeInsuranceSnoozeClick)
    dispatch(addSnoozeAnswer())
  }

  const handleNavToYourInsurance = () => {
    if (clientHasConfirmedPolicyDate) {
      return
    }
    dispatchEvent(ConfirmDetailsClick, {
      eventLabel: EVENTS_LABELS.HomeInsuranceModule,
    })
    history.push(OVERLAY_HELPERS.homeInsurance.buildPath('your-insurance'))
  }

  return (
    <div className={styles.container}>
      <Text
        size='l'
        className={styles.title}
        variant='neutralCool'
        variantCount='900'
      >
        <FormattedMessage
          id='Homeowner.homeInsurance.goodToGo'
          defaultMessage='Are you good to go?'
        />
      </Text>

      <div className={styles.buttonsContainer}>
        <button
          className={classnames(
            styles.buttonContainer,
            clientHasConfirmedPolicyDate && styles.disabled
          )}
          data-qa='nav-to-your-insurance'
          onClick={() => {
            ampli?.insuranceReviewed({
              insurance_policy_checked:
                AmplitudeEventConstants.CONFIRM_INSURANCE_DETAILS,
            })
            handleNavToYourInsurance()
          }}
        >
          <img
            className={classnames(styles.buttonImg, styles.confirm)}
            src={confirmImg}
            alt='confirm'
          />
          <Text variant='neutralCool' variantCount='700' size='xs'>
            <FormattedMessage
              id='Homeowner.homeInsurance.updatedMyPolicy'
              defaultMessage='Yes! I updated my policy'
            />
          </Text>
          <If condition={!clientHasConfirmedPolicyDate}>
            <Text variant='secondary' variantCount='600' size='xs'>
              <FormattedMessage
                id='Homeowner.homeInsurance.confirmDetails'
                defaultMessage='Confirm details'
              />
            </Text>
          </If>
          <If condition={clientHasConfirmedPolicyDate}>
            <Text variant='neutralCool' variantCount='700' size='xs'>
              <IconCheckCircle />
              <FormattedMessage
                id='Homeowner.homeInsurance.policyDetailsConfirmed'
                defaultMessage='Confirmed'
              />
            </Text>
          </If>
        </button>

        <button
          className={classnames(
            styles.buttonContainer,
            reminderSnoozed && styles.disabled
          )}
          onClick={() => {
            ampli?.insuranceReviewed({
              insurance_policy_checked:
                AmplitudeEventConstants.SNOOZE_INSURANCE_DETAILS,
            })
            handleAddHomeAnswer()
          }}
          data-qa='snooze-policy-reminder'
        >
          <img
            className={classnames(styles.buttonImg, styles.snooze)}
            src={snoozeImg}
            alt='snooze'
          />
          <Text variant='neutralCool' variantCount='700' size='xs'>
            <FormattedMessage
              id='Homeowner.homeInsurance.nextYear'
              defaultMessage='All good - remind me next year'
            />
          </Text>
          <If condition={!reminderSnoozed}>
            <Text variant='secondary' variantCount='600' size='xs'>
              <FormattedMessage
                id='Homeowner.homeInsurance.snooze'
                defaultMessage='Snooze'
              />
            </Text>
          </If>
          <If condition={reminderSnoozed}>
            <Text variant='neutralCool' variantCount='700' size='xs'>
              <IconCheckCircle />
              <FormattedMessage
                id='Homeowner.homeInsurance.snoozed'
                defaultMessage='Snoozed'
              />
            </Text>
          </If>
        </button>
      </div>
    </div>
  )
}

export default ConfirmOrSnoozeSection
